import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { DATE_FORMATS } from '@callmart/types';
import Container from '../components/Layout/Container';
import { getAllBookingDeltaByBookingId } from '../services/bookingDelta';
import DailyOSPBookingsTotals from '../components/DailyOSPBookings/DailyOSPBookingsTotals';
import CustomDatePicker from '../components/UI/CustomDatePicker';
import DailyOSPBookingsHistory from '../components/DailyOSPBookings/DailyOSPBookingsHistory';
import { createTableRows } from '../utils/agGridUtils';
import CardPopover from '../components/UI/CardPopover';
import { useCellPopover } from '../hooks/useCellPopover';
import {
    useCookieWorktype,
    useWorktypes,
    useHEMTLocations,
    useTotalForecastDeltasByDateRangeAndWorktype,
    useTotalBookedBookingsByDateRangeAndWorktype,
    useAllDistinctLocations,
    useLocationsByWorktype,
    useAllAvailableBookingsByDateAndWorktype,
    useOldestAndLatestBookedBookingsByDateAndWorktype,
    useAllBookedBookingsByDateRangeGroupByDateAndLocation,
} from '../hooks';
import { WorktypeSelectOptions } from '../enums/WorktypeSelectOptions';
import WorktypeSelect from '../components/Selects/WorktypeSelect';
import { setUploadedMonthAndYearToDate } from '../utils/dateUtils';
import { appTimezone } from '../config';
import TableGridItem from '../components/UI/TableGridItem';
import { createColumnDefs, createMapWithTableRows } from '../components/DailyOSPBookings/helpers/utils';
import { useSocket } from '../store/SocketContext';
import { SocketEvents } from '../enums/SocketEvents';
import dayjs from '@utils/dayjs';

const DailyOSPBookings = () => {
    const { t } = useTranslation('allBookings');
    const socket = useSocket();
    const gridRef = useRef(null);

    const [selectedDate, setSelectedDate] = useState(dayjs());
    const dateFrom = useMemo(() => dayjs(selectedDate).tz(appTimezone).startOf('day'), [selectedDate]);
    const dateTo = useMemo(() => dayjs(selectedDate).tz(appTimezone).endOf('day'), [selectedDate]);

    const [worktypeOptions] = useWorktypes({ prependAllWorktypesOption: true });
    const [selectedWorktype, setSelectedWorktype] = useCookieWorktype(worktypeOptions);

    const { showPopover, hoveredCell, hoveredCellHistoryList, popoverOpen, popoverClose } = useCellPopover();

    const forecastDeltasTotal = useTotalForecastDeltasByDateRangeAndWorktype({
        dateFrom,
        dateTo,
        worktype: selectedWorktype,
    });
    const totalForecastDeltas = useMemo(() => forecastDeltasTotal?.total || 0, [forecastDeltasTotal]);
    const bookedBookingsTotal = useTotalBookedBookingsByDateRangeAndWorktype({
        dateFrom,
        dateTo,
        worktype: selectedWorktype,
    });
    const totalBookedBookings = useMemo(() => bookedBookingsTotal?.total || 0, [bookedBookingsTotal]);
    const totalAvailableBookings = useMemo(
        () => totalForecastDeltas - totalBookedBookings,
        [totalForecastDeltas, totalBookedBookings]
    );

    const allLocationsFromHEMT = useHEMTLocations();
    const allDistinctLocations = useAllDistinctLocations();
    const locationsByWorktype = useLocationsByWorktype(selectedWorktype);
    const locationsForColumnDefs = useMemo(
        () =>
            selectedWorktype === WorktypeSelectOptions.ALL_WORKTYPES.value ? allDistinctLocations : locationsByWorktype,
        [selectedWorktype, allDistinctLocations, locationsByWorktype]
    );
    const columnDefs = useMemo(
        () =>
            allLocationsFromHEMT && locationsForColumnDefs?.length > 0
                ? createColumnDefs(locationsForColumnDefs, allLocationsFromHEMT, t)
                : [],
        [allLocationsFromHEMT, locationsForColumnDefs, t]
    );

    const { data: availableBookings } = useAllAvailableBookingsByDateAndWorktype({
        dateFrom,
        dateTo,
        worktype: selectedWorktype,
    });

    const bookedBookingsForAllWorktypes = useAllBookedBookingsByDateRangeGroupByDateAndLocation({
        dateFrom,
        dateTo,
    });
    const bookedBookingsForSelectedWorktype = useOldestAndLatestBookedBookingsByDateAndWorktype({
        dateFrom,
        dateTo,
        worktype: selectedWorktype,
    });
    const bookedBookings = useMemo(
        () =>
            selectedWorktype === WorktypeSelectOptions.ALL_WORKTYPES.value
                ? bookedBookingsForAllWorktypes
                : bookedBookingsForSelectedWorktype,
        [bookedBookingsForAllWorktypes, bookedBookingsForSelectedWorktype, selectedWorktype]
    );

    const tableRows = useMemo(
        () =>
            availableBookings?.length > 0 || bookedBookings?.length > 0
                ? createTableRows(createMapWithTableRows(availableBookings, bookedBookings), 'slotTime')
                : [],
        [availableBookings, bookedBookings]
    );

    const cellHovered = useCallback(
        async (params) => {
            if (params.column.colId.startsWith('oldestBookedBookingValueLocationId')) {
                const locationId = params.column.colId.replace('oldestBookedBookingValueLocationId', '');
                if (params.data[`oldestBookedBookingValueLocationId${locationId}`]) {
                    const bookingDeltasList = await getAllBookingDeltaByBookingId(
                        params.data[`bookingIdLocationId${locationId}`]
                    );

                    popoverOpen(true, params.event.target, bookingDeltasList);
                }
            }
        },
        [popoverOpen]
    );

    const handleDatePickerChange = useCallback((newValue) => {
        setSelectedDate(newValue);
    }, []);

    useEffect(() => {
        const onUploadedForecastDelta = ({ monthForUploading }) => {
            const uploadedDate = setUploadedMonthAndYearToDate(dayjs(), monthForUploading);
            setSelectedDate(uploadedDate);
            setSelectedWorktype(-1);
        };
        socket.on(SocketEvents.uploadedForecastDelta, onUploadedForecastDelta);

        const onCreatedBookingDelta = ({ monthForCreatedBookings, worktypeForCreatedBookings }) => {
            const uploadedDate = dayjs().month(monthForCreatedBookings - 1);
            setSelectedDate(uploadedDate);
            setSelectedWorktype(worktypeForCreatedBookings);
        };
        socket.on(SocketEvents.createdBookingDelta, onCreatedBookingDelta);

        return () => {
            socket.off(SocketEvents.uploadedForecastDelta, onUploadedForecastDelta);
            socket.off(SocketEvents.createdBookingDelta, onCreatedBookingDelta);
        };
    }, [setSelectedWorktype, socket]);

    return (
        <Container>
            <Grid container spacing={1}>
                <Grid item xs={12} md={3} lg={2}>
                    <CustomDatePicker
                        date={selectedDate}
                        inputFormat={DATE_FORMATS.DATE}
                        mask="__.__.____"
                        onChange={handleDatePickerChange}
                    ></CustomDatePicker>
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                    <WorktypeSelect
                        selectedWorktype={selectedWorktype}
                        worktypeOptions={worktypeOptions}
                        onChange={setSelectedWorktype}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={7}>
                    <DailyOSPBookingsTotals
                        bookedCalls={totalBookedBookings}
                        availableCalls={totalAvailableBookings}
                        forecastCalls={totalForecastDeltas}
                    />
                </Grid>
                <TableGridItem
                    ref={gridRef}
                    headerHeight={'0'}
                    groupHeaderHeight={'50'}
                    rowData={tableRows}
                    defaultColDef={{
                        editable: false,
                        filter: false,
                        sortable: false,
                        suppressHeaderMenuButton: true,
                    }}
                    columnDefs={columnDefs}
                    onCellMouseOver={cellHovered}
                ></TableGridItem>
            </Grid>
            {showPopover && (
                <CardPopover anchorEl={hoveredCell} onClose={popoverClose}>
                    <DailyOSPBookingsHistory bookingDeltasHistory={hoveredCellHistoryList} />
                </CardPopover>
            )}
        </Container>
    );
};

export default DailyOSPBookings;
